import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  FaPlus,
  FaSearch,
  FaHandshake,
  FaUsers,
  FaEnvelope,
  FaBuilding,
  FaSyncAlt,
} from "react-icons/fa";
import ButtonEdit from "../../../../components/Button/ButtonEdit";
import ButtonDelete from "../../../../components/Button/ButtonDelete";
import {
  getAllParrainages,
  deleteParrainage,
  createParrainage,
  updateParrainage,
} from "../../../../services/parrainageServices";
import { ToastContainer, toast } from "react-toastify";
import AddParrainageModal from "./AddParrainageModal";

const CARD =
  "rounded-[26px] border border-slate-200/80 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 shadow-[0_18px_50px_-20px_rgba(15,23,42,0.28)] backdrop-blur";
const HERO =
  "relative overflow-hidden rounded-[30px] bg-slate-950 text-white border border-slate-800 shadow-[0_25px_70px_-25px_rgba(15,23,42,0.85)]";
const INPUT =
  "w-full rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 outline-none transition placeholder:text-slate-400 focus:border-indigo-400 focus:ring-4 focus:ring-indigo-100 dark:focus:ring-indigo-900/40";
const BUTTON_SOFT =
  "inline-flex items-center justify-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-medium transition";

function StatCard({ icon, label, value, hint, dark = false }) {
  return (
    <div
      className={
        dark
          ? "rounded-[22px] border border-white/15 bg-white/10 p-4 backdrop-blur-md"
          : "rounded-[22px] border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 shadow-sm"
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div
            className={`text-xs uppercase tracking-[0.18em] ${
              dark ? "text-white/60" : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {label}
          </div>
          <div
            className={`mt-2 text-2xl font-semibold ${
              dark ? "text-white" : "text-slate-900 dark:text-slate-50"
            }`}
          >
            {value}
          </div>
          {hint ? (
            <div
              className={`mt-1 text-xs ${
                dark ? "text-white/70" : "text-slate-500 dark:text-slate-400"
              }`}
            >
              {hint}
            </div>
          ) : null}
        </div>

        <div
          className={`rounded-2xl p-3 ${
            dark
              ? "bg-white/10 text-white"
              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200"
          }`}
        >
          {icon}
        </div>
      </div>
    </div>
  );
}

function SectionHead({ eyebrow, title, description, action }) {
  return (
    <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        {eyebrow ? (
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
            {eyebrow}
          </div>
        ) : null}
        <h2 className="mt-1 text-xl font-semibold text-slate-900 dark:text-slate-50">
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {description}
          </p>
        ) : null}
      </div>
      {action ? <div>{action}</div> : null}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="p-10 text-center">
      <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-2xl dark:bg-slate-800">
        🤝
      </div>
      <div className="text-lg font-semibold text-slate-900 dark:text-slate-50">
        Aucun parrainage trouvé
      </div>
      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
        Ajoute un premier parrainage ou élargis la recherche.
      </div>
    </div>
  );
}

function SkeletonList() {
  return (
    <div className="p-5 space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}

function Th({ children, className = "" }) {
  return (
    <th
      className={`whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400 ${className}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className = "" }) {
  return (
    <td className={`px-4 py-4 align-top text-sm text-slate-700 dark:text-slate-200 ${className}`}>
      {children}
    </td>
  );
}

function Badge({ children, tone = "gray" }) {
  const tones = {
    gray: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    blue: "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
    purple: "bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300",
  };

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${tones[tone] || tones.gray}`}>
      {children}
    </span>
  );
}

const ParrainagePage = () => {
  const [parrainages, setParrainages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editParrainageData, setEditParrainageData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchParrainages = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllParrainages();
      setParrainages(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur lors du chargement des parrainages:", error);
      toast.error("Erreur lors du chargement des données.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchParrainages();
  }, [fetchParrainages]);

  const filteredParrainages = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return parrainages.filter((item) => {
      if (!term) return true;

      const haystack = [
        item.nom_parrain,
        item.prenom_parrain,
        item.club_parrain,
        item.email_parrain,
        item.club_parraine,
        item.nom_contact_parraine,
        item.prenom_contact_parraine,
        item.email_parraine,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(term);
    });
  }, [parrainages, searchTerm]);

  const stats = useMemo(() => {
    const uniqueParrains = new Set();
    const uniqueClubsParrains = new Set();
    const uniqueClubsParraines = new Set();
    let emailsCount = 0;

    filteredParrainages.forEach((item) => {
      uniqueParrains.add(
        `${item.nom_parrain || ""}-${item.prenom_parrain || ""}`.toLowerCase()
      );
      if (item.club_parrain) uniqueClubsParrains.add(item.club_parrain.toLowerCase());
      if (item.club_parraine) uniqueClubsParraines.add(item.club_parraine.toLowerCase());
      if (item.email_parrain || item.email_parraine) emailsCount += 1;
    });

    return {
      total: filteredParrainages.length,
      parrains: uniqueParrains.size,
      clubsParrains: uniqueClubsParrains.size,
      clubsParraines: uniqueClubsParraines.size,
      withEmails: emailsCount,
    };
  }, [filteredParrainages]);

  const handleDelete = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce parrainage ?")) {
      return;
    }

    try {
      await deleteParrainage(id);
      toast.success("Parrainage supprimé avec succès !");
      setParrainages((prev) => prev.filter((parrainage) => parrainage.id !== id));
    } catch (error) {
      console.error("Erreur lors de la suppression du parrainage:", error);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleEdit = (id) => {
    const parrainageToEdit = parrainages.find((p) => p.id === id);
    setEditParrainageData(parrainageToEdit || null);
    setIsModalOpen(true);
  };

  const handleAddOrEdit = async (formData) => {
    try {
      if (editParrainageData) {
        await updateParrainage(editParrainageData.id, formData);
        toast.success("Parrainage modifié avec succès !");
      } else {
        await createParrainage(formData);
        toast.success("Parrainage ajouté avec succès !");
      }

      await fetchParrainages();
      setEditParrainageData(null);
      return true;
    } catch (error) {
      console.error("Erreur lors de l'enregistrement :", error);
      toast.error("Erreur lors de l'enregistrement.");
      return false;
    }
  };

  const isEmpty = !loading && filteredParrainages.length === 0;

  return (
    <div className="space-y-6">
      {/* HERO */}
      <section className={HERO}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(129,140,248,0.35),transparent_30%),radial-gradient(circle_at_top_right,rgba(236,72,153,0.30),transparent_25%),radial-gradient(circle_at_bottom_left,rgba(34,197,94,0.22),transparent_30%)]" />
        <div className="relative p-6 md:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
                <FaHandshake />
                Réseau & recommandations
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight md:text-4xl">
                Gestion des parrainages
              </h1>

              <p className="mt-3 max-w-2xl text-sm leading-6 text-white/75 md:text-base">
                Suis les clubs parrains, les clubs parrainés, leurs contacts et
                garde une vue claire sur ton réseau de recommandation.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => {
                  setEditParrainageData(null);
                  setIsModalOpen(true);
                }}
                className={`${BUTTON_SOFT} bg-white text-slate-900 hover:bg-slate-100`}
              >
                <FaPlus />
                Ajouter un parrainage
              </button>

              <button
                type="button"
                onClick={fetchParrainages}
                className={`${BUTTON_SOFT} bg-white/10 text-white hover:bg-white/20 border border-white/15`}
              >
                <FaSyncAlt />
                Rafraîchir
              </button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            <StatCard
              dark
              icon={<FaHandshake />}
              label="Parrainages"
              value={stats.total}
              hint="Résultats affichés"
            />
            <StatCard
              dark
              icon={<FaUsers />}
              label="Parrains"
              value={stats.parrains}
              hint="Contacts identifiés"
            />
            <StatCard
              dark
              icon={<FaBuilding />}
              label="Clubs parrains"
              value={stats.clubsParrains}
              hint="Origine des recommandations"
            />
            <StatCard
              dark
              icon={<FaBuilding />}
              label="Clubs parrainés"
              value={stats.clubsParraines}
              hint="Clubs recommandés"
            />
            <StatCard
              dark
              icon={<FaEnvelope />}
              label="Contacts e-mail"
              value={stats.withEmails}
              hint="Parrain ou filleul"
            />
          </div>
        </div>
      </section>

      {/* FILTRES */}
      <section className={`${CARD} p-5 md:p-6`}>
        <SectionHead
          eyebrow="Recherche"
          title="Retrouver rapidement un parrainage"
          description="Recherche par nom, prénom, club ou e-mail."
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="relative md:col-span-2">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${INPUT} pl-11`}
              type="text"
              placeholder="Rechercher un parrain, un club, un contact, un e-mail…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
            <span>{filteredParrainages.length} résultat(s)</span>
            {searchTerm ? <Badge tone="blue">Filtré</Badge> : <Badge>Global</Badge>}
          </div>
        </div>
      </section>

      {/* TABLE / CARDS */}
      <section className={`${CARD} overflow-hidden`}>
        <div className="border-b border-slate-200 dark:border-slate-800 px-5 py-4 md:px-6">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
                Liste des parrainages
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Vue d’ensemble des parrains, clubs référents et contacts parrainés.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="purple">{filteredParrainages.length} affiché(s)</Badge>
            </div>
          </div>
        </div>

        {loading ? (
          <SkeletonList />
        ) : isEmpty ? (
          <EmptyState />
        ) : (
          <>
            {/* desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-[1100px] w-full">
                <thead className="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <Th>Parrain</Th>
                    <Th>Club parrain</Th>
                    <Th>Email parrain</Th>
                    <Th>Club parrainé</Th>
                    <Th>Contact parrainé</Th>
                    <Th>Email parrainé</Th>
                    <Th className="text-right">Actions</Th>
                  </tr>
                </thead>

                <tbody>
                  {filteredParrainages.map((parrainage) => (
                    <tr
                      key={parrainage.id}
                      className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition"
                    >
                      <Td className="min-w-[220px]">
                        <div className="font-semibold text-slate-900 dark:text-slate-50">
                          {parrainage.nom_parrain} {parrainage.prenom_parrain}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {parrainage.telephone_parrain || "Téléphone non renseigné"}
                        </div>
                      </Td>

                      <Td className="min-w-[180px]">
                        <Badge tone="green">{parrainage.club_parrain || "—"}</Badge>
                      </Td>

                      <Td className="min-w-[220px] text-slate-500 dark:text-slate-400">
                        {parrainage.email_parrain || "—"}
                      </Td>

                      <Td className="min-w-[180px]">
                        <Badge tone="blue">{parrainage.club_parraine || "—"}</Badge>
                      </Td>

                      <Td className="min-w-[220px]">
                        <div className="font-medium text-slate-900 dark:text-slate-50">
                          {parrainage.nom_contact_parraine}{" "}
                          {parrainage.prenom_contact_parraine}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          {parrainage.telephone_parraine || "Téléphone non renseigné"}
                        </div>
                      </Td>

                      <Td className="min-w-[220px] text-slate-500 dark:text-slate-400">
                        {parrainage.email_parraine || "—"}
                      </Td>

                      <Td className="min-w-[110px]">
                        <div className="flex justify-end gap-2">
                          <ButtonEdit
                            onClick={() => handleEdit(parrainage.id)}
                            hoverLabel="Éditer"
                          />
                          <ButtonDelete
                            onClick={() => handleDelete(parrainage.id)}
                            hoverLabel="Supprimer"
                          />
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* mobile */}
            <div className="grid gap-4 p-4 md:hidden">
              {filteredParrainages.map((parrainage) => (
                <div
                  key={parrainage.id}
                  className="rounded-[24px] border border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/40 p-4"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-slate-900 dark:text-slate-50">
                        {parrainage.nom_parrain} {parrainage.prenom_parrain}
                      </div>
                      <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                        {parrainage.club_parrain || "Club parrain non renseigné"}
                      </div>
                    </div>

                    <Badge tone="green">Parrain</Badge>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <MobileInfo
                      label="E-mail parrain"
                      value={parrainage.email_parrain || "—"}
                    />
                    <MobileInfo
                      label="Téléphone parrain"
                      value={parrainage.telephone_parrain || "—"}
                    />
                    <MobileInfo
                      label="Club parrainé"
                      value={parrainage.club_parraine || "—"}
                    />
                    <MobileInfo
                      label="Contact parrainé"
                      value={`${parrainage.nom_contact_parraine || ""} ${
                        parrainage.prenom_contact_parraine || ""
                      }`.trim() || "—"}
                    />
                    <MobileInfo
                      label="E-mail parrainé"
                      value={parrainage.email_parraine || "—"}
                    />
                    <MobileInfo
                      label="Téléphone parrainé"
                      value={parrainage.telephone_parraine || "—"}
                    />
                  </div>

                  <div className="mt-4 flex gap-2">
                    <ButtonEdit
                      onClick={() => handleEdit(parrainage.id)}
                      hoverLabel="Éditer"
                    />
                    <ButtonDelete
                      onClick={() => handleDelete(parrainage.id)}
                      hoverLabel="Supprimer"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <AddParrainageModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditParrainageData(null);
        }}
        onAdd={handleAddOrEdit}
        initialData={editParrainageData}
      />

      <ToastContainer />
    </div>
  );
};

function MobileInfo({ label, value }) {
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 p-3">
      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 font-medium text-slate-900 dark:text-slate-50">
        {value}
      </div>
    </div>
  );
}

export default ParrainagePage;